import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLanguage, languages } from '../contexts/LanguageContext'
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion'
import { usePresence } from '../hooks/usePresence'
import { menuSize, placeAnchoredMenu } from '../utils/menuPlacement'
import './LanguageSelector.css'

function LanguageSelector({ position = 'right', variant = 'icon' }) {
  const { language, setLanguage } = useLanguage()
  const [isOpen, setIsOpen] = useState(false)
  const [coords, setCoords] = useState({ left: 0, top: 0 })
  const menuRef = useRef(null)
  const frostRef = useRef(null)
  const buttonRef = useRef(null)
  const reducedMotion = usePrefersReducedMotion()
  const { mounted, leaving } = usePresence(isOpen, { duration: 180, reduced: reducedMotion })

  useLayoutEffect(() => {
    if (!mounted) return undefined
    const update = () => {
      const trigger = buttonRef.current?.getBoundingClientRect()
      if (!trigger) return
      const size = menuSize(menuRef.current)
      const placed = placeAnchoredMenu({
        trigger,
        width: size.width,
        height: size.height,
        vw: window.innerWidth,
        vh: window.innerHeight,
        align: position === 'left' ? 'start' : 'end',
        prefer: 'below',
      })
      setCoords((prev) => (
        prev.left === placed.left && prev.top === placed.top ? prev : placed
      ))
      if (frostRef.current) {
        void getComputedStyle(frostRef.current).backdropFilter
      }
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [mounted, position, language])

  useEffect(() => {
    if (!mounted) return undefined
    const onPointer = (event) => {
      if (menuRef.current?.contains(event.target) || buttonRef.current?.contains(event.target)) return
      setIsOpen(false)
    }
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [mounted])

  const handleLanguageChange = (langCode) => {
    setLanguage(langCode)
    setIsOpen(false)
  }

  const toolsItem = variant === 'tools-item'

  return (
    <div className={`language-selector-container${toolsItem ? ' is-tools-item' : ''}`}>
      {toolsItem ? (
        <button
          ref={buttonRef}
          type="button"
          role="menuitem"
          className="glass-menu-item tools-language-item"
          onClick={() => setIsOpen((open) => !open)}
          title="Changer de langue"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <img src="/Icon language.png" alt="" className="glass-menu-icon" />
          <span className="glass-menu-label">Langue</span>
          <span className="glass-menu-trail">{languages[language]?.name}</span>
        </button>
      ) : (
        <button
          ref={buttonRef}
          type="button"
          className="language-selector-btn"
          onClick={() => setIsOpen((open) => !open)}
          title="Changer de langue"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <img src="/Icon language.png" alt="" />
          <span className="sr-only">Changer de langue</span>
        </button>
      )}
      {mounted && typeof document !== 'undefined' ? createPortal(
        <div className="language-menu-root" style={{ left: coords.left, top: coords.top }}>
          <div className="lg-blur-prewarm" aria-hidden="true" />
          <div
            ref={menuRef}
            className={`language-menu lg-panel${leaving ? ' is-leaving' : ''}`}
            role="listbox"
            aria-label="Langue"
          >
            <div className="lg-panel-frost" ref={frostRef} />
            <div className="lg-panel-tint" />
            <div className="lg-panel-shine" />
            <div className={`lg-panel-body language-menu-motion${reducedMotion ? '' : leaving ? ' is-leaving' : ' is-animated'}`}>
              {Object.entries(languages).map(([code, lang]) => (
                <button
                  key={code}
                  type="button"
                  role="option"
                  aria-selected={language === code}
                  className={`language-option${language === code ? ' active' : ''}`}
                  onClick={() => handleLanguageChange(code)}
                >
                  <span className="language-flag">{lang.flag}</span>
                  <span className="language-name">{lang.name}</span>
                  {language === code ? <span className="language-check">✓</span> : null}
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body,
      ) : null}
    </div>
  )
}

export default LanguageSelector
